// src/context/FirebaseAuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    onAuthStateChanged,
    signOut as firebaseSignOut,
    sendEmailVerification,
    updateProfile
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

const FirebaseAuthContext = createContext();

export const useFirebaseAuth = () => {
    const context = useContext(FirebaseAuthContext);
    if (!context) {
        throw new Error('useFirebaseAuth must be used within FirebaseAuthProvider');
    }
    return context;
};

export const FirebaseAuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setLoading(true);

            if (firebaseUser) {
                setUser(firebaseUser);

                // Fetch additional user data from Firestore
                try {
                    const userDocRef = doc(db, 'users', firebaseUser.uid);
                    const userDoc = await getDoc(userDocRef);

                    if (userDoc.exists()) {
                        setUserData(userDoc.data());
                    } else {
                        // Create user document if it doesn't exist
                        const newUserData = {
                            uid: firebaseUser.uid,
                            email: firebaseUser.email,
                            full_name: firebaseUser.displayName || '',
                            role: 'candidate',
                            created_at: new Date().toISOString(),
                            email_verified: firebaseUser.emailVerified,
                            is_active: true
                        };
                        setUserData(newUserData);
                    }
                } catch (err) {
                    console.error('Error fetching user data:', err);
                    setError(err.message);
                }
            } else {
                setUser(null);
                setUserData(null);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const signOut = async () => {
        try {
            await firebaseSignOut(auth);
            setUser(null);
            setUserData(null);
            return true;
        } catch (error) {
            console.error('Error signing out:', error);
            setError(error.message);
            return false;
        }
    };

    const resendVerificationEmail = async () => {
        if (user && !user.emailVerified) {
            try {
                await sendEmailVerification(user);
                return true;
            } catch (error) {
                console.error('Error sending verification email:', error);
                setError(error.message);
                return false;
            }
        }
        return false;
    };

    const updateUserData = async (updates) => {
        if (!user) return false;

        try {
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, {
                ...updates,
                updated_at: new Date().toISOString()
            });

            // Update local state
            setUserData(prev => ({ ...prev, ...updates }));

            // Update profile if needed
            if (updates.full_name) {
                await updateProfile(user, { displayName: updates.full_name });
            }

            return true;
        } catch (error) {
            console.error('Error updating user data:', error);
            setError(error.message);
            return false;
        }
    };

    const value = {
        user,
        userData,
        loading,
        error,
        signOut,
        resendVerificationEmail,
        updateUserData,
        isAuthenticated: !!user,
        isEmailVerified: user?.emailVerified || false,
        userRole: userData?.role || null
    };

    return (
        <FirebaseAuthContext.Provider value={value}>
            {children}
        </FirebaseAuthContext.Provider>
    );
};